import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import SummaryApi from "../common";

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)",
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionBlue: "#2563EB",
  actionBlueHover: "#1D4ED8",
  actionGreen: "#10B981",
  actionRed: "#EF4444",
};

const TeamleadUploadExcelPromotionData = () => {
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [fileRowCount, setFileRowCount] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [skippedData, setSkippedData] = useState([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [insertedMessage, setInsertedMessage] = useState("");
  const [isInserted, setIsInserted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [allSkipped, setAllSkipped] = useState(false);
  const [insertedCount, setInsertedCount] = useState(0);
  const fileInputRef = useRef(null);

  const escapeHtml = (str) => {
    if (!str) return '';
    return str.replace(/[&<>]/g, function (m) {
      if (m === '&') return '&amp;';
      if (m === '<') return '&lt;';
      if (m === '>') return '&gt;';
      return m;
    });
  };

  const parseFile = (selectedFile) => {
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx" && fileExtension !== "xls" && fileExtension !== "csv") {
      setError("Please upload a valid Excel file (.xlsx, .xls) or CSV file (.csv).");
      setFile(null);
      return;
    }
    setError("");
    setFile(selectedFile);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        let jsonData;

        if (fileExtension === "csv") {
          const csvData = e.target.result;
          const workbook = XLSX.read(csvData, { type: "string" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(sheet);
        } else {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(sheet);
        }

        setFileRowCount(jsonData.length);
        setFileData(jsonData);
      } catch (err) {
        setError("Error parsing file. Please check the file format.");
        setFile(null);
      }
    };

    if (fileExtension === "csv") {
      reader.readAsText(selectedFile);
    } else {
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) parseFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) parseFile(dropped);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleRemoveFile = () => {
    setFile(null);
    setFileData(null);
    setFileRowCount(null);
    setError("");
    setIsInserted(false);
    setInsertedMessage("");
    setSkippedData([]);
    setSkippedCount(0);
    setAllSkipped(false);
    setInsertedCount(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadNewFile = () => {
    handleRemoveFile();
    setAllSkipped(false);
    setIsInserted(false);
    setInsertedCount(0);
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      { mobile_number: "9876543210", status: "Active", promotion_date: "2026-07-15" },
      { mobile_number: "9123456789", status: "Pending", promotion_date: "2026-07-20" },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Format");
    XLSX.writeFile(wb, "promotion_student_sample.xlsx");
    toast.success("Sample template downloaded successfully!");
  };

  const openSkippedDataInNewTab = (skippedRecords) => {
    if (!skippedRecords || skippedRecords.length === 0) return;

    const newWindow = window.open();
    if (!newWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for this site.");
      return;
    }

    const tableRows = skippedRecords.map((record, idx) => {
      const mobileNumber = record.mobile_number || '-';
      const reason = record.reason || 'Duplicate';
      return `
        <tr>
          <td>${idx + 1}</td>
          <td style="font-family: monospace;">${escapeHtml(mobileNumber)}</td>
          <td><span class="reason-badge">${escapeHtml(reason)}</span></td>
        </tr>
      `;
    }).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Skipped Records - Promotional Student Data</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              background: linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
              min-height: 100vh;
              padding: 40px 24px;
            }
            .container { max-width: 1200px; margin: 0 auto; }
            .header {
              background: white;
              border-radius: 20px;
              padding: 24px 32px;
              margin-bottom: 24px;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
              gap: 16px;
            }
            .title-section h1 { font-size: 1.75rem; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
            .title-section p { color: #64748b; font-size: 0.875rem; }
            .badge { background: #fee2e2; color: #dc2626; padding: 8px 16px; border-radius: 40px; font-weight: 600; font-size: 0.875rem; }
            .card { background: white; border-radius: 20px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05); overflow: hidden; }
            .table-wrapper { overflow-x: auto; padding: 0; }
            table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
            th { background: #f8fafc; padding: 14px 16px; text-align: left; font-weight: 600; color: #172C57; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
            td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; color: #334155; }
            tr:hover td { background: #f8fafc; }
            .reason-badge { background: #fee2e2; color: #b91c1c; padding: 4px 12px; border-radius: 30px; font-size: 0.75rem; font-weight: 500; display: inline-block; }
            .footer { margin-top: 24px; text-align: center; color: #94a3b8; font-size: 0.75rem; }
            .btn-download {
              background: #eff6ff; border: none; padding: 10px 20px; border-radius: 12px;
              color: #2563eb; font-weight: 500; font-size: 0.875rem; cursor: pointer;
              display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s;
            }
            .btn-download:hover { background: #dbeafe; }
            @media (max-width: 640px) {
              body { padding: 20px 16px; }
              th, td { padding: 10px 12px; }
              .header { padding: 18px 20px; }
              .title-section h1 { font-size: 1.35rem; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="title-section">
                <h1>Skipped Records</h1>
                <p>These entries already exist in the system and were not imported</p>
              </div>
              <div style="display: flex; gap: 12px; align-items: center;">
                <span class="badge">${skippedRecords.length} Records Skipped</span>
                <button class="btn-download" onclick="downloadExcel()">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                  Download Excel
                </button>
              </div>
            </div>
            <div class="card">
              <div class="table-wrapper">
                <table>
                  <thead>
                    <tr><th>S.NO</th><th>Mobile Number</th><th>Reason</th></tr>
                  </thead>
                  <tbody>${tableRows}</tbody>
                </table>
              </div>
            </div>
            <div class="footer"><p>Promotional Student Data Upload · Duplicate entries are automatically skipped</p></div>
          </div>
          <script>
            const skippedData = ${JSON.stringify(skippedRecords)};
            function downloadExcel() {
              if (!skippedData || skippedData.length === 0) return;
              const script = document.createElement('script');
              script.src = 'https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js';
              script.onload = () => {
                const ws = XLSX.utils.json_to_sheet(skippedData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Skipped Records');
                XLSX.writeFile(wb, 'skipped_promotional_student_data.xlsx');
              };
              document.body.appendChild(script);
            }
          </script>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Please upload an Excel or CSV file.");
      return;
    }

    setLoading(true);
    setError("");
    setAllSkipped(false);

    const formData = new FormData();
    formData.append("excel_file", file);

    try {
      const response = await fetch(SummaryApi.uploadPromotionStudentExcel.url, {
        method: SummaryApi.uploadPromotionStudentExcel.method,
        headers: {
          Authorization: "Bearer sandeep_university_916",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        const insertedCnt = data.inserted_count || 0;
        const skippedCnt = data.skipped?.count || 0;

        setInsertedCount(insertedCnt);

        if (skippedCnt === 0) {
          const successMsg = data.message || `${insertedCnt} records inserted successfully`;

          setIsInserted(true);
          setInsertedMessage(successMsg);
          setError("");
          toast.success(successMsg);

          setTimeout(() => {
            window.location.href = "/promotion-excel-report";
          }, 1000);

          setLoading(false);
          return;
        }

        if (skippedCnt > 0) {
          setIsInserted(false);
          setAllSkipped(true);
          setError(`Some records already exist (${skippedCnt} duplicates found)`);

          setSkippedData(data.skipped.data);
          setSkippedCount(skippedCnt);

          toast.error(`${skippedCnt} duplicate records found`);

          openSkippedDataInNewTab(data.skipped.data);

          setLoading(false);
          return;
        }
      } else {
        toast.error(data.message || "Error inserting promotional data.");
        setError(data.message || "Error inserting promotional data.");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Server error. Please try again later.");
      setError("Server error. Please try again later.");
      setLoading(false);
    }
  };

  const handleDownloadSkippedExcel = () => {
    if (!skippedData || skippedData.length === 0) return;
    const sheet = XLSX.utils.json_to_sheet(skippedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Skipped Records");
    const excelFile = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelFile], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "skipped_promotional_student_data.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileTypeText = () => {
    if (!file) return "";
    const extension = file.name.split(".").pop().toLowerCase();
    if (extension === "csv") return "CSV File";
    return "Excel File";
  };

  return (
    <div className="min-h-screen mt-4">
      <div className="">

        {/* Header card — full width */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-5 mb-6">
          <div className="flex items-center gap-4">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-md shrink-0"
              style={{ backgroundColor: THEME.primary }}
            >
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1
                className="text-xl md:text-2xl font-bold tracking-tight"
                style={{ color: THEME.primary }}
              >
                Upload Student Promotion Excel Data
              </h1>
              <p className="text-gray-500 text-sm mt-0.5">
                Upload your Excel or CSV file to import promotional records
              </p>
            </div>
          </div>
        </div>

        {/* 2-column layout: form on left (2/3), tips on right (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT: Upload form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              {!file ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-300 ${dragOver
                    ? "border-[#172C57] bg-slate-50"
                    : "border-gray-300 hover:border-[#172C57] hover:bg-slate-50"
                    }`}
                >
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center transition-colors duration-300"
                      style={{
                        backgroundColor: dragOver ? THEME.primaryLight : "#F3F4F6",
                      }}
                    >
                      <svg
                        className="w-7 h-7 transition-colors duration-300"
                        style={{ color: dragOver ? THEME.primary : "#9CA3AF" }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-700 font-semibold text-base">Drop your Excel or CSV file here</p>
                      <p className="text-gray-400 text-sm mt-1">or click to browse &nbsp;·&nbsp; .xlsx, .xls, .csv supported</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="border rounded-xl p-4"
                  style={{
                    borderColor: "rgba(23, 44, 87, 0.2)",
                    backgroundColor: THEME.primaryLight,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                      style={{ backgroundColor: THEME.primary }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-sm font-semibold truncate">{file.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-medium" style={{ color: THEME.primary }}>
                          {getFileTypeText()}
                        </span>
                        <span className="text-gray-400 text-xs">•</span>
                        <p className="text-xs font-medium" style={{ color: THEME.primary }}>
                          {fileRowCount !== null ? `${fileRowCount.toLocaleString()} records found` : "Reading..."}
                        </p>
                      </div>
                    </div>
                    {!isInserted && (
                      <button
                        onClick={handleRemoveFile}
                        className="w-8 h-8 rounded-lg bg-white hover:bg-red-100 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all duration-200 flex-shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {fileData && fileData.length > 0 && (
                <div className="mt-5">
                  <p
                    className="text-xs font-semibold uppercase tracking-widest mb-3"
                    style={{ color: THEME.primary }}
                  >
                    Preview — First 5 Records
                  </p>
                  <div className="rounded-xl overflow-hidden border border-gray-200 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-200">
                        <tr>
                          {Object.keys(fileData[0]).map((key) => (
                            <th
                              key={key}
                              className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider whitespace-nowrap"
                              style={{ color: THEME.primary }}
                            >
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {fileData.slice(0, 5).map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            {Object.keys(row).map((key) => (
                              <td key={key} className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{row[key]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-4">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              )}

              {isInserted && !allSkipped && (
                <div className="flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mt-4">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-green-600 text-sm font-medium">{insertedMessage}</p>
                  {skippedCount > 0 && (
                    <button
                      onClick={handleDownloadSkippedExcel}
                      className="ml-auto text-xs underline underline-offset-2 whitespace-nowrap font-semibold"
                      style={{ color: THEME.actionBlue }}
                    >
                      Download {skippedCount} skipped
                    </button>
                  )}
                </div>
              )}

              {!allSkipped && (
                <button
                  onClick={handleSubmit}
                  disabled={!file || loading || isInserted}
                  style={{
                    backgroundColor: !file || loading || isInserted ? "#E5E7EB" : THEME.primary,
                  }}
                  onMouseEnter={(e) => {
                    if (file && !loading && !isInserted) e.currentTarget.style.opacity = "0.9";
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  className={`w-full mt-6 py-3.5 rounded-xl font-semibold text-sm transition-opacity flex items-center justify-center gap-2 ${!file || loading || isInserted ? "text-gray-400 cursor-not-allowed" : "text-white shadow-sm hover:shadow-md"
                    }`}
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading...
                    </>
                  ) : isInserted ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uploaded Successfully
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Promotional Data
                    </>
                  )}
                </button>
              )}
            </div>

            <p className="text-center text-gray-400 text-xs mt-4">
              Duplicate entries will be skipped automatically · Only .xlsx, .xls, and .csv files accepted
            </p>
          </div>

          {/* RIGHT: Required columns + tips */}
          <div className="lg:col-span-1 space-y-4">

            {/* Required Excel Columns card */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ backgroundColor: THEME.primary }}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-white font-semibold text-sm">Required Excel Columns</span>
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: THEME.primary }}>Mobile Number</th>
                  </tr>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap text-green-600">Status</th>
                  </tr>
                  <tr className="bg-gray-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap text-purple-600">Promotion Date</th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Sample download card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <p
                className="text-xs font-semibold uppercase tracking-wide mb-2"
                style={{ color: THEME.primary }}
              >
                Download Template
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Get a sample Excel file with the correct column format.
              </p>
              <button
                onClick={downloadSampleExcel}
                style={{ backgroundColor: THEME.actionBlue }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
                className="w-full text-white text-xs font-semibold py-2.5 px-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Sample Excel
              </button>
            </div>

            {/* Tips card */}
            <div
              className="rounded-2xl p-4 border"
              style={{
                backgroundColor: THEME.primaryLight,
                borderColor: "rgba(23, 44, 87, 0.15)",
              }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-wide mb-2"
                style={{ color: THEME.primary }}
              >
                Tips
              </p>
              <ul className="text-xs text-gray-700 space-y-1.5">
                <li className="flex gap-1.5">
                  <span style={{ color: THEME.primary }}>•</span>
                  <span>Duplicate mobile numbers are skipped automatically.</span>
                </li>
                <li className="flex gap-1.5">
                  <span style={{ color: THEME.primary }}>•</span>
                  <span>Only the first sheet in the file is processed.</span>
                </li>
                <li className="flex gap-1.5">
                  <span style={{ color: THEME.primary }}>•</span>
                  <span>Preview shows the first 5 rows only.</span>
                </li>
                <li className="flex gap-1.5">
                  <span style={{ color: THEME.primary }}>•</span>
                  <span>You'll be redirected to the report page on success.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamleadUploadExcelPromotionData;